import type { IntegrationItem } from '@/features/Setting/Integrations/types';
import { SOURCE_SEPERATOR } from '@/types/commonConst';
import { getFeedbackSourceName } from '@/types/feedbackConst';
import { FEEDBACK_COLUMNS, SOURCE_COLUMNS } from '@/types/filterKeyConst';
import { deleteObjectField } from '@/utils/object';

import { STRUCTURED_FILTER_OPERATOR } from '../model/constants';
import {
  getMinMaxColumnKey,
  getOrigianlColumnFromRangeColumnKey,
  rangeFilterKeyList,
} from './range-filter';
import {
  buildStructuredFilter,
  validateEmptyFilter,
} from './structured-filter';

const excludeDataFilterKeys: string[] = [
  FEEDBACK_COLUMNS.DATE_START,
  FEEDBACK_COLUMNS.DATE_END,
  FEEDBACK_COLUMNS.SNIPPET,
  FEEDBACK_COLUMNS.FEEDBACK_TEXT,
  FEEDBACK_COLUMNS.ENTITY_GROUP,
];

class SFilter {
  private filter: StructuredFilter = {};

  constructor(props?: StructuredFilter) {
    this.filter = props ? { ...props } : {};
  }

  get(): StructuredFilter | undefined {
    return validateEmptyFilter(this.filter);
  }

  removeField(key: string): StructuredFilter | undefined {
    this.filter = deleteObjectField(this.filter, key);
    return this.get();
  }

  getDataFilterMap(): Record<string, StructuredFilterStringNumberValueSet[]> {
    return Object.keys(this.filter).reduce(
      (acc, key) => {
        if (excludeDataFilterKeys.includes(key)) {
          return acc;
        }
        const value = this.filter[key];
        if (Array.isArray(value)) {
          acc[key] = value as StructuredFilterStringNumberValueSet[];
        }
        return acc;
      },
      {} as Record<string, StructuredFilterStringNumberValueSet[]>,
    );
  }

  getSinlgeDataFilter(key: string): StructuredFilterStringNumberValueSet[] {
    if (!this.filter[key]) return [];
    if (!Array.isArray(this.filter[key])) {
      throw new Error('invalid filter type');
    }
    return [...this.filter[key]] as StructuredFilterStringNumberValueSet[];
  }

  getDateFilter(): NullableDateRangeT {
    const start_date = this.filter[FEEDBACK_COLUMNS.DATE_START];
    const end_date = this.filter[FEEDBACK_COLUMNS.DATE_END];

    if (typeof start_date !== 'string' && typeof start_date !== 'undefined') {
      throw new Error(
        `[getDateFilter] Invalid filter type 'start_date' : ${start_date}`,
      );
    }

    if (typeof end_date !== 'string' && typeof end_date !== 'undefined') {
      throw new Error(
        `[getDateFilter] Invalid filter type 'end_date' : ${end_date}`,
      );
    }

    return {
      [FEEDBACK_COLUMNS.DATE_START]: start_date || null,
      [FEEDBACK_COLUMNS.DATE_END]: end_date || null,
    };
  }

  getIncludeSnippetFilter(): StructuredFilterStringNumberValueSet | null {
    return (
      this.getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET).find(
        (item) =>
          item.op === STRUCTURED_FILTER_OPERATOR.AND ||
          item.op === STRUCTURED_FILTER_OPERATOR.OR,
      ) || null
    );
  }

  getExcludeSnippetFilter(): StructuredFilterStringNumberValueSet | null {
    return (
      this.getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET).find(
        (item) => item.op === STRUCTURED_FILTER_OPERATOR.NOR,
      ) || null
    );
  }

  getIncludeTextFilter(): StructuredFilterStringNumberValueSet | null {
    return (
      this.getSinlgeDataFilter(FEEDBACK_COLUMNS.FEEDBACK_TEXT).find(
        (item) =>
          item.op === STRUCTURED_FILTER_OPERATOR.AND ||
          item.op === STRUCTURED_FILTER_OPERATOR.OR,
      ) || null
    );
  }

  getExcludeTextFilter(): StructuredFilterStringNumberValueSet | null {
    return (
      this.getSinlgeDataFilter(FEEDBACK_COLUMNS.FEEDBACK_TEXT).find(
        (item) => item.op === STRUCTURED_FILTER_OPERATOR.NOR,
      ) || null
    );
  }

  getSearchKeywordFilter(): StructuredFilterStringNumberValueSet | null {
    return this.getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET)[0] || null;
  }

  getRestFilterMap(): StructuredFilter {
    return Object.keys(this.filter).reduce((acc, key) => {
      if (excludeDataFilterKeys.includes(key)) return acc;
      acc[key] = this.filter[key];
      return acc;
    }, {} as StructuredFilter);
  }

  addValueSet(
    key: string,
    filter: StructuredFilterStringNumberValueSet,
  ): StructuredFilter | undefined {
    if (filter.values.length === 0) return this.filter;
    if (!this.filter[key]) {
      this.filter[key] = [filter];
      return this.filter;
    }
    if (!Array.isArray(this.filter[key])) {
      throw new Error('invalid filter type');
    }
    this.filter[key].push(filter);
    return this.get();
  }

  addSourceValueSet(
    filter: StructuredFilterSourceObjectSet,
  ): StructuredFilter | undefined {
    if (filter.values.length === 0) return this.filter;
    if (!this.filter[SOURCE_COLUMNS.SOURCE_TYPE]) {
      this.filter[SOURCE_COLUMNS.SOURCE_TYPE] = [
        {
          op: filter.op,
          values: filter.values,
        },
      ];
      return this.filter;
    }
    if (!Array.isArray(this.filter[SOURCE_COLUMNS.SOURCE_TYPE])) {
      throw new Error('invalid filter type');
    }
    this.filter[SOURCE_COLUMNS.SOURCE_TYPE].push(filter);
  }

  addFilterQueryFormats(filterQueryFormats: StructuredFilterQueryFormatT[]) {
    const newFilters = buildStructuredFilter(filterQueryFormats);
    const restFilters = this.getRestFilterMap();

    this.filter = {
      ...restFilters,
      ...newFilters,
    };
  }

  setValueSet(
    key: string,
    filter: StructuredFilterStringNumberValueSet[],
  ): StructuredFilter | undefined {
    this.filter[key] = filter;
    return this.get();
  }

  setDateFilter(dateRange: NullableDateRangeT): StructuredFilter | undefined {
    if (dateRange.start_date) {
      this.filter[FEEDBACK_COLUMNS.DATE_START] = dateRange.start_date;
    }
    if (dateRange.end_date) {
      this.filter[FEEDBACK_COLUMNS.DATE_END] = dateRange.end_date;
    }
    return this.get();
  }

  setIncludeSnippetFilter(
    filter: StructuredFilterStringNumberValueSet | null,
  ): StructuredFilter | undefined {
    const prevFilter = this.getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET);
    const filterValueSetIndex = prevFilter.findIndex(
      (item) =>
        item.op === STRUCTURED_FILTER_OPERATOR.AND ||
        item.op === STRUCTURED_FILTER_OPERATOR.OR,
    );
    if (!filter) {
      if (filterValueSetIndex !== -1) {
        prevFilter.splice(filterValueSetIndex, 1);
        if (prevFilter.length === 0) {
          this.filter = deleteObjectField(
            this.filter,
            FEEDBACK_COLUMNS.SNIPPET,
          );
        } else {
          this.filter[FEEDBACK_COLUMNS.SNIPPET] = prevFilter;
        }
      }
    } else if (filterValueSetIndex !== -1) {
      prevFilter.splice(filterValueSetIndex, 1, filter);
      this.filter[FEEDBACK_COLUMNS.SNIPPET] = prevFilter;
    } else {
      this.addValueSet(FEEDBACK_COLUMNS.SNIPPET, filter);
    }

    return this.get();
  }

  setExcludeSnippetFilter(
    filter: StructuredFilterStringNumberValueSet | null,
  ): StructuredFilter | undefined {
    const prevFilter = this.getSinlgeDataFilter(FEEDBACK_COLUMNS.SNIPPET);
    const filterValueSetIndex = prevFilter.findIndex(
      (item) => item.op === STRUCTURED_FILTER_OPERATOR.NOR,
    );
    if (!filter) {
      if (filterValueSetIndex !== -1) {
        prevFilter.splice(filterValueSetIndex, 1);
        if (prevFilter.length === 0) {
          this.filter = deleteObjectField(
            this.filter,
            FEEDBACK_COLUMNS.SNIPPET,
          );
        } else {
          this.filter[FEEDBACK_COLUMNS.SNIPPET] = prevFilter;
        }
      }
    } else if (filterValueSetIndex !== -1) {
      prevFilter.splice(filterValueSetIndex, 1, filter);
      this.filter[FEEDBACK_COLUMNS.SNIPPET] = prevFilter;
    } else {
      this.addValueSet(FEEDBACK_COLUMNS.SNIPPET, filter);
    }
    return this.get();
  }

  setIncludeTextFilter(
    filter: StructuredFilterStringNumberValueSet | null,
  ): StructuredFilter | undefined {
    const prevFilter = this.getSinlgeDataFilter(FEEDBACK_COLUMNS.FEEDBACK_TEXT);
    const filterValueSetIndex = prevFilter.findIndex(
      (item) =>
        item.op === STRUCTURED_FILTER_OPERATOR.AND ||
        item.op === STRUCTURED_FILTER_OPERATOR.OR,
    );
    if (!filter) {
      if (filterValueSetIndex !== -1) {
        prevFilter.splice(filterValueSetIndex, 1);
        if (prevFilter.length === 0) {
          this.filter = deleteObjectField(
            this.filter,
            FEEDBACK_COLUMNS.FEEDBACK_TEXT,
          );
        } else {
          this.filter[FEEDBACK_COLUMNS.FEEDBACK_TEXT] = prevFilter;
        }
      }
    } else if (filterValueSetIndex !== -1) {
      prevFilter.splice(filterValueSetIndex, 1, filter);
      this.filter[FEEDBACK_COLUMNS.FEEDBACK_TEXT] = prevFilter;
    } else {
      this.addValueSet(FEEDBACK_COLUMNS.FEEDBACK_TEXT, filter);
    }

    return this.get();
  }

  setExcludeTextFilter(
    filter: StructuredFilterStringNumberValueSet | null,
  ): StructuredFilter | undefined {
    const prevFilter = this.getSinlgeDataFilter(FEEDBACK_COLUMNS.FEEDBACK_TEXT);
    const filterValueSetIndex = prevFilter.findIndex(
      (item) => item.op === STRUCTURED_FILTER_OPERATOR.NOR,
    );
    if (!filter) {
      if (filterValueSetIndex !== -1) {
        prevFilter.splice(filterValueSetIndex, 1);
        if (prevFilter.length === 0) {
          this.filter = deleteObjectField(
            this.filter,
            FEEDBACK_COLUMNS.FEEDBACK_TEXT,
          );
        } else {
          this.filter[FEEDBACK_COLUMNS.FEEDBACK_TEXT] = prevFilter;
        }
      }
    } else if (filterValueSetIndex !== -1) {
      prevFilter.splice(filterValueSetIndex, 1, filter);
      this.filter[FEEDBACK_COLUMNS.FEEDBACK_TEXT] = prevFilter;
    } else {
      this.addValueSet(FEEDBACK_COLUMNS.FEEDBACK_TEXT, filter);
    }
    return this.get();
  }

  isEmptyDataFilter(): boolean {
    const dataFilter = this.getDataFilterMap();
    for (const key in dataFilter) {
      for (const filterValues of dataFilter[key]) {
        if (filterValues.values.length > 0) {
          return false;
        }
      }
    }
    return true;
  }

  extractRangeFilter(): StructuredFilterQueryFormatT[] {
    const rangeQueryFormats: StructuredFilterQueryFormatT[] = [];
    rangeFilterKeyList.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(this.filter, key)) return;
      const originalColumnKey = getOrigianlColumnFromRangeColumnKey(key);
      const { min, max } = getMinMaxColumnKey(originalColumnKey);

      const minValue = this.filter[min] as number | undefined;
      const maxValue = this.filter[max] as number | undefined;

      rangeQueryFormats.push({
        columnKey: originalColumnKey,
        filterValues: [],
        operator: STRUCTURED_FILTER_OPERATOR.AND,
        rangeValues: {
          min: minValue,
          max: maxValue,
        },
      });

      this.removeField(min);
      this.removeField(max);
    });

    return rangeQueryFormats;
  }

  extractSourceFilter(
    sourceInfo: IntegrationItem[],
  ): StructuredFilterQueryFormatT[] {
    const structuredFilter = this.get();
    if (!structuredFilter) return [];
    const result: StructuredFilterQueryFormatT[] = [];
    const sources = structuredFilter[
      SOURCE_COLUMNS.SOURCE_TYPE
    ] as StructuredFilterSourceObjectSet[];

    if (!sources) return [];
    for (const source of sources) {
      const filterValues = source.values.map((value) => {
        const sourceValue = value;
        const type = sourceValue.type;
        const domain = sourceValue.domain;
        const channel = sourceValue.channel;
        const valueArray = [];
        const nameArray = [];

        const sourceItem = sourceInfo?.find((info) => info.source === type);
        const domainItem = sourceItem?.domains?.find(
          (domainItem) => domainItem.id === domain,
        );
        const channelItem = domainItem?.channels?.find(
          (channelItem) => channelItem.id === channel,
        );
        if (sourceItem) {
          valueArray.push(sourceItem.source);
          nameArray.push(getFeedbackSourceName(sourceItem.source));
        }
        if (domainItem) {
          valueArray.push(domainItem.id);
          nameArray.push(domainItem.name);
        }
        if (channelItem) {
          valueArray.push(channelItem.id);
          nameArray.push(channelItem.name);
        }
        return {
          filterKey: valueArray.join(SOURCE_SEPERATOR),
          filterName: nameArray?.[nameArray.length - 1] || '',
        };
      });
      result.push({
        columnKey: SOURCE_COLUMNS.SOURCE_TYPE,
        filterValues,
        operator: source.op,
      });
    }

    this.removeField(SOURCE_COLUMNS.SOURCE_TYPE);

    return result;
  }
}

export default SFilter;
